/**
 * Overlap-free placement for the two floating layers of the workflow builder
 * canvas: the node hover card and the per-edge condition labels.
 *
 * Both are pure geometry over rectangles in unzoomed canvas coordinates, so the
 * canvas can render them without measuring the DOM — and so the rules can be
 * tested (see ./workflowCanvasLayout.test.js).
 */

/**
 * Hover card box and its clearance from the node it describes.
 *
 * `h` is only the fallback height. The card grows to fit its description, so
 * callers pass the real height to `placeHoverCard` — a taller card has to be
 * placed against the space it will actually occupy, or it runs off the canvas.
 * `maxH` caps that growth so one long description cannot fill the viewport.
 */
export const HOVER_CARD = { w: 224, h: 96, maxH: 280, gap: 14, lineH: 14.5, charsPerLine: 36 };

/**
 * Rough height for a card, from the description it has to show. Text metrics
 * are not available here (this is pure geometry, no DOM), so the line count is
 * estimated from character count at `charW` — close enough to place the card,
 * and the card itself scrolls nothing: it simply renders to its natural height.
 */
export const hoverCardHeight = (description = "", { hasRole, hasArea, fromLibrary } = {}) => {
  const head = 16 + (hasRole ? 13 : 0) + (hasArea ? 12 : 0) + (fromLibrary ? 13 : 0);
  // ~202px of text width at 10px, which wraps near 36 characters. Deliberately
  // pessimistic: over-estimating leaves a little empty card, whereas
  // under-estimating clips the description the card exists to show.
  const perLine = Math.max(1, Math.ceil(String(description || "").length / HOVER_CARD.charsPerLine));
  const body = description ? 5 + perLine * HOVER_CARD.lineH : 0;
  return Math.min(HOVER_CARD.maxH, Math.round(18 + head + body));
};

/** A node's `+` ports stick out this far — treat it as part of the node. */
export const PORT_HALO = 10;

/**
 * Edge label box. `lane` is the vertical step a colliding label slides by; the
 * three widths are tried in order, so a long condition narrows (and ellipsises)
 * rather than growing into its neighbour.
 */
export const CHIP = { h: 20, lane: 24, widths: [176, 118, 82], pad: 18, charW: 5.7 };

export const rectsOverlap = (a, b) =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

export const overlapArea = (a, b) => {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
};

export const inflate = (r, by) => ({ x: r.x - by, y: r.y - by, w: r.w + by * 2, h: r.h + by * 2 });

const totalOverlap = (rect, obstacles) => obstacles.reduce((s, o) => s + overlapArea(rect, o), 0);

/** Width the label needs for `text`, capped at `max` (the text truncates there). */
export const chipTextWidth = (text, max = CHIP.widths[0]) =>
  Math.max(CHIP.pad + 24, Math.min(max, CHIP.pad + String(text || "").length * CHIP.charW));

/**
 * Picks the side of `node` for the hover card: right, left, below, then above —
 * the first one that covers no obstacle and stays inside the canvas wins, so the
 * card never hides a node, another node's ports, or an edge label.
 *
 * `obstacles` are rects already inflated by `PORT_HALO` (include the hovered
 * node itself: its own ports must stay clickable). `bounds` may start left of
 * the graph (`{ x: -60, w: … }`) to lend the card the empty canvas beside a
 * narrow graph. Falls back to the least-overlapping side, clamped inside the
 * bounds, when everything is packed.
 *
 * @returns {{x: number, y: number, w: number, h: number, side: string}}
 */
export function placeHoverCard(node, obstacles = [], bounds = { w: 0, h: 0 }, height) {
  const { w, gap } = HOVER_CARD;
  const h = height || HOVER_CARD.h;
  const x0 = bounds.x || 0, y0 = bounds.y || 0;
  const x1 = x0 + (bounds.w || 0), y1 = y0 + (bounds.h || 0);
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, Math.max(lo, hi)));
  const insetX = v => clamp(v, x0 + 4, x1 - w - 4);
  const insetY = v => clamp(v, y0 + 4, y1 - h - 4);

  const midY = insetY(node.y + node.h / 2 - h / 2);

  /*
   * Sides are tried beside the node before above or below it. Edges leave a
   * node's top and bottom, so a card placed there lands on the connections even
   * when it clears every node box — which is why the card appeared to sit on
   * top of the edges. To the side it is out of the edges' way by construction.
   *
   * When it must go above or below, it is offset clear of the node's own centre
   * line rather than centred on it, so the edge running out of that face stays
   * visible beside the card instead of behind it.
   */
  const offX = insetX(node.x + node.w / 2 - w / 2 + w * 0.34);
  const candidates = [
    { side: "right", x: node.x + node.w + gap, y: midY },
    { side: "left", x: node.x - gap - w, y: midY },
    { side: "top", x: offX, y: node.y - gap - h },
    { side: "bottom", x: offX, y: node.y + node.h + gap },
  ];

  let best = null;
  for (const c of candidates) {
    const rect = { x: c.x, y: c.y, w, h };
    const outside = c.x < x0 || c.y < y0 || c.x + w > x1 || c.y + h > y1;
    const cost = totalOverlap(rect, obstacles) + (outside ? 1e6 : 0);
    if (cost === 0) return { ...rect, side: c.side };
    if (!best || cost < best.cost) best = { ...c, cost };
  }
  return { x: insetX(best.x), y: insetY(best.y), w, h, side: best.side };
}

/**
 * Obstacle rects approximating a set of routed edges, so the hover card can be
 * kept off the connections rather than only off the node boxes.
 *
 * `polylines` are arrays of sampled `{x, y}` points (the routers already produce
 * these to test their own routes). Each consecutive pair becomes a small box, so
 * a curve is covered by a chain of them.
 */
export const edgeObstacles = (polylines = [], thickness = 7) =>
  polylines.flatMap((pts) => {
    const out = [];
    for (let i = 1; i < (pts?.length || 0); i++) {
      const a = pts[i - 1], b = pts[i];
      out.push({
        x: Math.min(a.x, b.x) - thickness,
        y: Math.min(a.y, b.y) - thickness,
        w: Math.abs(b.x - a.x) + thickness * 2,
        h: Math.abs(b.y - a.y) + thickness * 2,
      });
    }
    return out;
  });

/**
 * Places every edge label at its edge midpoint, then resolves collisions by
 * sliding a label up or down along its own edge (`yMin`..`yMax`, the band
 * between the two nodes it joins) and, only if no lane is free, by narrowing it.
 *
 * Labels are placed by rank, then in reading order — top row first, then left to
 * right — so the result is stable: the same graph always lays out the same way.
 * A lower `rank` picks its lane first; the canvas ranks the labels it always
 * shows (conditional edges) above the ones that only appear on hover.
 *
 * @param {Array} items  `{ uid, text, x, y, yMin, yMax, rank }` — x/y is the midpoint.
 * @param {Array} obstacles Rects labels must also avoid (node boxes).
 * @returns {Object} uid → `{ x, y, w }`, x/y still being the label's centre.
 */
export function layoutEdgeLabels(items, obstacles = []) {
  const lanes = [0, 1, -1, 2, -2, 3, -3];
  const ordered = [...items].sort((a, b) =>
    ((a.rank || 0) - (b.rank || 0)) || (a.y - b.y) || (a.x - b.x) || String(a.uid).localeCompare(String(b.uid))
  );
  const placed = [...obstacles];
  const out = {};

  for (const item of ordered) {
    // a degenerate band (same row, e.g. while a cycle is being drawn) pins the label
    const band = [item.yMin ?? -Infinity, item.yMax ?? Infinity].sort((a, b) => a - b);
    let best = null;

    for (const max of CHIP.widths) {
      const w = chipTextWidth(item.text, max);
      for (const lane of lanes) {
        const y = Math.max(band[0], Math.min(item.y + lane * CHIP.lane, band[1]));
        const rect = { x: item.x - w / 2, y: y - CHIP.h / 2, w, h: CHIP.h };
        const cost = totalOverlap(rect, placed);
        if (cost === 0) { best = { rect, w, y }; break; }
        if (!best || cost < best.cost) best = { rect, w, y, cost };
      }
      if (best && best.cost === undefined) break;   // clean placement found
    }

    placed.push(best.rect);
    out[item.uid] = { x: item.x, y: best.y, w: best.w };
  }
  return out;
}
